import { useState, useCallback, useRef, useEffect } from 'react';
import { calendarService, CalendarEvent } from '../services/calendarService';
import { RecruitingNcaaDivision, recruitingEventMatchesDivision } from '../utils/recruitingCalendar';

type UseCalendarOptions = {
    signedInWithGoogle: boolean;
    recruitingSportId: number | null;
    /** When true, pass sportId to the recruiting-events API (staff/admin sport filter). */
    passRecruitingSportId?: boolean;
    recruitingDivision?: RecruitingNcaaDivision;
};

type CalendarState = {
    events: CalendarEvent[];
    personalEventsLoading: boolean;
    companyEventsLoading: boolean;
    recruitingEventsLoading: boolean;
    polarisTeamEventsLoading: boolean;
    personalEventsError: string | null;
    companyEventsError: string | null;
    recruitingEventsError: string | null;
    recruitingEventsNotice: string | null;
    showPersonal: boolean;
    showCompany: boolean;
    showPolaris: boolean;
    showTeam: boolean;
    showRecruiting: boolean;
};

export const useCalendar = ({
    signedInWithGoogle,
    recruitingSportId,
    passRecruitingSportId = false,
    recruitingDivision = 'D1',
}: UseCalendarOptions) => {
    const [state, setState] = useState<CalendarState>({
        events: [],
        personalEventsLoading: false,
        companyEventsLoading: false,
        recruitingEventsLoading: false,
        polarisTeamEventsLoading: false,
        personalEventsError: null,
        companyEventsError: null,
        recruitingEventsError: null,
        recruitingEventsNotice: null,
        showPersonal: signedInWithGoogle,
        showCompany: true,
        showPolaris: true,
        showTeam: true,
        showRecruiting: false,
    });

    const rangeRef = useRef<{ start: string; end: string } | null>(null);
    const fetchGenerationRef = useRef(0);

    const fetchEvents = useCallback(
        async (startISO: string, endISO: string) => {
            rangeRef.current = { start: startISO, end: endISO };
            const generation = ++fetchGenerationRef.current;
            const isCurrent = () => generation === fetchGenerationRef.current;

            const fetchPersonal = signedInWithGoogle && state.showPersonal;
            const fetchCompany = state.showCompany;
            const fetchRecruiting = state.showRecruiting && recruitingSportId != null && recruitingSportId > 0;
            const fetchPolarisTeam = state.showPolaris || state.showTeam;

            const promises: Promise<CalendarEvent[]>[] = [];

            if (fetchPolarisTeam) {
                setState(prev => ({ ...prev, polarisTeamEventsLoading: true }));
                promises.push(
                    calendarService.fetchPolarisAndTeamEvents(startISO, endISO).catch(() => {
                        if (!isCurrent()) {
                            return [];
                        }
                        return [];
                    })
                );
            }

            if (fetchPersonal) {
                setState(prev => ({ ...prev, personalEventsLoading: true, personalEventsError: null }));
                promises.push(
                    calendarService.fetchPersonalEvents(startISO, endISO).catch(error => {
                        if (!isCurrent()) {
                            return [];
                        }
                        setState(prev => ({
                            ...prev,
                            personalEventsError: error.message || 'Failed to load personal events',
                        }));
                        return [];
                    })
                );
            }

            if (fetchCompany) {
                setState(prev => ({ ...prev, companyEventsLoading: true, companyEventsError: null }));
                promises.push(
                    calendarService.fetchCompanyEvents(startISO, endISO).catch(error => {
                        if (!isCurrent()) {
                            return [];
                        }
                        setState(prev => ({
                            ...prev,
                            companyEventsError: error.message || 'Failed to load company events',
                        }));
                        return [];
                    })
                );
            }

            if (fetchRecruiting) {
                setState(prev => ({
                    ...prev,
                    recruitingEventsLoading: true,
                    recruitingEventsError: null,
                    recruitingEventsNotice: null,
                }));
                promises.push(
                    calendarService
                        .fetchRecruitingEvents(
                            startISO,
                            endISO,
                            passRecruitingSportId ? recruitingSportId ?? undefined : undefined,
                            recruitingDivision
                        )
                        .then((result) => {
                            if (!isCurrent()) {
                                return [];
                            }
                            if (result.unavailable) {
                                setState(prev => ({
                                    ...prev,
                                    recruitingEventsNotice:
                                        'A recruiting calendar is not available for this sport yet.',
                                }));
                            }
                            return result.events;
                        })
                        .catch(() => {
                        if (!isCurrent()) {
                            return [];
                        }
                        setState(prev => ({
                            ...prev,
                            recruitingEventsError:
                                'We could not load the recruiting calendar. Please try again.',
                        }));
                        return [];
                    })
                );
            }

            try {
                const results = await Promise.all(promises);
                if (!isCurrent()) {
                    return;
                }
                const mergedEvents = results.flat();

                setState(prev => ({
                    ...prev,
                    events: mergedEvents,
                    personalEventsLoading: false,
                    companyEventsLoading: false,
                    recruitingEventsLoading: false,
                    polarisTeamEventsLoading: false,
                }));
            } catch {
                if (!isCurrent()) {
                    return;
                }
                setState(prev => ({
                    ...prev,
                    personalEventsLoading: false,
                    companyEventsLoading: false,
                    recruitingEventsLoading: false,
                    polarisTeamEventsLoading: false,
                }));
            }
        },
        [signedInWithGoogle, recruitingSportId, passRecruitingSportId, recruitingDivision, state.showPersonal, state.showCompany, state.showRecruiting, state.showPolaris, state.showTeam]
    );

    const refetchEvents = useCallback(() => {
        if (rangeRef.current) {
            fetchEvents(rangeRef.current.start, rangeRef.current.end);
        }
    }, [fetchEvents]);

    useEffect(() => {
        if (rangeRef.current) {
            fetchEvents(rangeRef.current.start, rangeRef.current.end);
        }
    }, [state.showPersonal, state.showCompany, state.showRecruiting, fetchEvents]);

    const setShowPersonal = useCallback((show: boolean) => {
        setState(prev => ({ ...prev, showPersonal: show }));
    }, []);

    const setShowCompany = useCallback((show: boolean) => {
        setState(prev => ({ ...prev, showCompany: show }));
    }, []);

    const setShowPolaris = useCallback((show: boolean) => {
        setState(prev => ({ ...prev, showPolaris: show }));
    }, []);

    const setShowTeam = useCallback((show: boolean) => {
        setState(prev => ({ ...prev, showTeam: show }));
    }, []);

    const setShowRecruiting = useCallback((show: boolean) => {
        setState(prev => ({ ...prev, showRecruiting: show }));
    }, []);

    const isLoading =
        state.personalEventsLoading ||
        state.companyEventsLoading ||
        state.recruitingEventsLoading ||
        state.polarisTeamEventsLoading;

    const filteredEvents = state.events.filter(
        (ev) =>
            (state.showPolaris && ev.source?.toLowerCase() === 'polaris') ||
            (state.showTeam && ev.source?.toLowerCase() === 'team') ||
            (state.showPersonal && ev.source?.toLowerCase() === 'personal') ||
            (state.showCompany && ev.source?.toLowerCase() === 'company') ||
            (state.showRecruiting && ev.source?.toLowerCase() === 'recruiting')
    );

    const recruitingEvents = state.events.filter(
        (ev) =>
            ev.source?.toLowerCase() === 'recruiting'
            && recruitingEventMatchesDivision(ev, recruitingDivision)
    );

    return {
        events: filteredEvents,
        recruitingEvents,
        isLoading,
        personalEventsError: state.personalEventsError,
        companyEventsError: state.companyEventsError,
        recruitingEventsError: state.recruitingEventsError,
        recruitingEventsNotice: state.recruitingEventsNotice,
        showPersonal: state.showPersonal,
        showCompany: state.showCompany,
        showPolaris: state.showPolaris,
        showTeam: state.showTeam,
        showRecruiting: state.showRecruiting,
        setShowPersonal,
        setShowCompany,
        setShowPolaris,
        setShowTeam,
        setShowRecruiting,
        fetchEvents,
        refetchEvents,
    };
};

